import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	Wrapper,
	Container,
	Title,
	Table,
	Actions,
	AbortPopup,
	ModalBody,
} from './Benchmark.styles';
import { AliasListTable } from './components/AliasListTable';
import {
	getAliasNameData,
	getBenchmarkCategoryData,
	getCompanyMetadataOptions,
	getPipelineStatus,
	saveBenchmarkSettings,
} from 'utils/apiRequests';
import {
	ApButtonSecondary,
	ApLoaderDefault,
	ApLoaderText,
} from '@alixpartners/ui-components';
import CategoryTree from './components/CategoryTree/CategoryTree';
import Modal from 'components/Modal';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions } from 'Investigate/utils';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { checkAllExist } from './utils';
import EditAlias from './components/EditAlias/EditAlias';
import Toast from 'components/Toast/Toast';
import { NotificationTypes } from 'components/Toast/constants';
import { SettingsContext } from 'contexts/SettingsContext';
import { ToastMessageProvider } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { isNull } from 'lodash';

// default format of benchmark settings
const initialSettings = {
	aliasName: [],
	categorySettings: {
		L1L3: [],
	},
};

const INITIAL_OPTIONS = {
	employeesNumberBands: [],
	geographies: [],
	headOfficeLocations: [],
	industries: [],
	revenues: [],
};

const getUpdatedMetadata = result => {
	return Object.keys(result).reduce((acc, key) => {
		return {
			...acc,
			[key]: result[key].data,
		};
	}, {});
};

//time for which toast is shown (in ms)
const toastTime = 7000;

const Benchmark = () => {
	const [isAliasFetching, setIsAliasFetching] = useState(true);
	const [isCategoryFetching, setIsCategoryFetching] = useState(false);
	const [aliasData, setAliasData] = useState(null);
	const [categoryTreeData, setCategoryTreeData] = useState([]);
	const [defaultSettings, setDefaultSettings] = useState(initialSettings);
	const [isSettingsSaving, setIsSettingsSaving] = useState(false);
	const [isCategoryTree, setIsCategoryTree] = useState(false);
	const [isEditAlias, setIsEditAlias] = useState(false);
	const [metadataOptions, setMetadataOptions] = useState(INITIAL_OPTIONS);
	const [aliasToEdit, setAliasToEdit] = useState({});
	const [isPipelineRunning, setIsPipelineRunning] = useState(false);
	const [isChecking, setIsChecking] = useState(true);
	const [toasts, setToasts] = useState([]);
	const { currency } = useContext(QueryStringContext);
	const { options } = useContext(SettingsOptionsContext);
	const { currency: currencyList } = options;
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const { hideDecimals } = useContext(SettingsContext);

	useEffect(() => {
		// call function to check whether the pipeline is running or not
		const checkIsPreccessing = async () => {
			const response = await getPipelineStatus();
			if (response.succeeded) {
				if (response.data.length > 0) {
					if (response.data[0].is_Processing) {
						setIsPipelineRunning(true);
					}
				}
				setIsChecking(false);
			}
		};
		checkIsPreccessing();
		// eslint-disable-next-line
	}, []);

	useEffect(
		() => {
			if (!isPipelineRunning) {
				fetchMetaDataAndSettings();
			}
		},
		[isPipelineRunning]
	);

	useEffect(
		() => {
			if (isCategoryTree) {
				const getFormattedPrice = value => {
					return formatSpendWithDecimalAndSymbol(
						value,
						currencySymbol,
						hideDecimals,
						2
					).toUpperCase();
				};

				const isCategoryDefault = (parentName, childName) => {
					const categoryData = defaultSettings.categorySettings.L1L3;
					const filteredData = categoryData.filter(
						el => el.parent === parentName && el.name === childName
					);
					return filteredData.length > 0 ? true : false;
				};

				const checkIsAllSelected = (items, parentName) => {
					const data = items.map(el => {
						return { name: el.name, parent: parentName };
					});
					const settingsData = defaultSettings.categorySettings.L1L3;
					return checkAllExist(data, settingsData);
				};

				const bodyParam = {
					alias: defaultSettings.aliasName,
					categoryType: 1,
				};
				setIsCategoryFetching(true);
				getBenchmarkCategoryData(bodyParam).then(response => {
					const treeData = [response.data].map(
						({ childs, rootName, tailSpend, totalSpend }) => {
							const items = tailSpend ? [...childs, tailSpend] : childs;
							const sortedMainItems = items.sort(
								(a, b) => b.totalSpend - a.totalSpend
							);
							return {
								data: sortedMainItems.map(
									({ currentLevel, nextLevel, totalSpend: childSpend }) => {
										const sortedSubItems = nextLevel.sort(
											(a, b) => b.spend - a.spend
										);
										return {
											name: currentLevel,
											value: getFormattedPrice(childSpend),
											isSelectAll:
												defaultSettings.categorySettings.L1L3.length > 0
													? checkIsAllSelected(sortedSubItems, currentLevel)
													: true,
											data: sortedSubItems.map(({ name, spend }) => ({
												name,
												id: `${rootName}/${currentLevel}/${name}`,
												parent: currentLevel,
												value: getFormattedPrice(spend),
												isChecked:
													defaultSettings.categorySettings.L1L3.length > 0
														? isCategoryDefault(currentLevel, name)
														: true,
											})),
										};
									}
								),
								total: {
									name: 'Indirect',
									id:
										rootName === 'Indirect'
											? 'CategorySpendTreeIndirect'
											: 'CategorySpendTreeDirect',
									value: getFormattedPrice(totalSpend),
								},
							};
						}
					);
					setCategoryTreeData(treeData);
					setIsCategoryFetching(false);
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[defaultSettings.aliasName, currency, currencySymbol, isCategoryTree]
	);

	const fetchMetaDataAndSettings = () => {
		setIsAliasFetching(true);
		setDefaultSettings(initialSettings);
		getAliasNameData().then(response => {
			if (response.succeeded) {
				if (response.data.metaDataOutputs !== null) {
					getCompanyMetadataOptions().then(metaDataOptions => {
						const metadata = getUpdatedMetadata(metaDataOptions);
						setMetadataOptions(metadata);
						if (!!metaDataOptions?.industries) {
							if (metaDataOptions.industries.data.length > 0) {
								const aliasRows = response.data.metaDataOutputs.map(el => {
									const filteredIndustry = metaDataOptions.industries.data.filter(
										item => item.key === el.industry
									);
									const filteredHeadofficeLocation = metaDataOptions.headOfficeLocations.data.filter(
										item => item.key === el.headOfficeLocation
									);
									return {
										aliasName: el.alias_Name,
										revenue: el.revenue,
										headCount: isNull(el.headCount) ? 0 : el.headCount,
										headOfficeLocationKey: el.headOfficeLocation,
										operatingcountries_AP_L1Code:
											el.operatingcountries_AP_L1Code,
										operatingcountries_AP_L2Code:
											el.operatingcountries_AP_L2Code,
										industryKey: el.industry,
										industryValue:
											filteredIndustry.length > 0
												? filteredIndustry[0].value
												: '',
										headOfficeLocationValue:
											filteredHeadofficeLocation.length > 0
												? filteredHeadofficeLocation[0].value
												: '',
									};
								});
								setAliasData(aliasRows);
								setIsAliasFetching(false);
							}
						}
					});
				} else {
					setAliasData([]);
					setIsAliasFetching(false);
				}
				if (response.data.settings !== null) {
					setDefaultSettings(response.data.settings);
				}
			} else {
				setIsAliasFetching(false);
			}
		});
	};

	const onRowSelection = useCallback(params => {
		const selectedData = params.api.getSelectedRows();
		const updatedData = selectedData.map(element => {
			return element.aliasName;
		});
		setDefaultSettings(prevState => ({
			...prevState,
			aliasName: updatedData,
		}));
	}, []);

	const onChangeSelectionAll = (value, flag) => {
		const parentIndex = categoryTreeData[0].data.findIndex(
			x => x.name === value
		);
		const dataRows = [...categoryTreeData];
		dataRows[0].data[parentIndex]['isSelectAll'] = flag;
		dataRows[0].data[parentIndex].data.forEach(element => {
			element.isChecked = flag;
		});
		setCategoryTreeData(dataRows);

		//update settings in state on selection
		let existingSettings = defaultSettings.categorySettings.L1L3;
		let newSettings = [];
		if (flag) {
			newSettings = dataRows[0].data[parentIndex].data.map(element => {
				return { name: element.name, parent: element.parent };
			});
		} else {
			existingSettings = existingSettings.filter(el => el.parent !== value);
		}

		const combinedSettings = [...existingSettings, ...newSettings];
		const uniqueValues = Array.from(
			new Set(combinedSettings.map(item => JSON.stringify(item)))
		).map(item => JSON.parse(item));

		setDefaultSettings(prevState => ({
			...prevState,
			categorySettings: {
				...prevState.categorySettings,
				L1L3: uniqueValues,
			},
		}));
	};

	const onChangeCategorySelection = (dataItem, flag) => {
		const parentData = categoryTreeData[0].data.filter(
			el => el.name === dataItem.parent
		);
		const parentIndex = categoryTreeData[0].data.findIndex(
			x => x.name === dataItem.parent
		);
		const childIndex = parentData[0].data.findIndex(
			x => x.name === dataItem.name
		);
		const dataRows = [...categoryTreeData];
		if (flag === false) {
			dataRows[0].data[parentIndex]['isSelectAll'] = flag;
		}
		dataRows[0].data[parentIndex].data[childIndex]['isChecked'] = flag;
		setCategoryTreeData(dataRows);

		//update settings in state on selection
		let settingsToUpdate;
		if (!flag) {
			settingsToUpdate = defaultSettings.categorySettings.L1L3.filter(
				el => el.name !== dataItem.name
			);
		} else {
			settingsToUpdate = defaultSettings.categorySettings.L1L3;
			settingsToUpdate.push({ name: dataItem.name, parent: dataItem.parent });
		}

		setDefaultSettings(prevState => ({
			...prevState,
			categorySettings: {
				...prevState.categorySettings,
				L1L3: settingsToUpdate,
			},
		}));
	};

	const onSaveSettings = () => {
		setIsSettingsSaving(true);
		saveBenchmarkSettings(defaultSettings).then(response => {
			if (response.succeeded) {
				setIsSettingsSaving(false);
				setIsCategoryTree(pervState => !pervState);
				showToast('Settings saved successfully.', NotificationTypes.success);
			}
		});
	};

	const onEditAlias = useCallback(params => {
		setAliasToEdit(params);
		setIsEditAlias(true);
	}, []);

	const onUpdateAlias = (key, value) => {
		if (key === 'industryKey') {
			const industryName = metadataOptions.industries.filter(
				el => el.key === value
			)[0].value;
			setAliasToEdit(prevState => ({
				...prevState,
				[key]: value,
				industryValue: industryName,
			}));
		} else if (key === 'revenue') {
			setAliasToEdit(prevState => ({
				...prevState,
				[key]: isNaN(value) ? 0 : value,
			}));
		}
	};

	const showToast = (message, status) => {
		const newToast = {
			id: Date.now(),
			message,
			status,
		};

		setToasts(prevToasts => [...prevToasts, newToast]);

		setTimeout(() => {
			setToasts(prevToasts =>
				prevToasts.filter(toast => toast.id !== newToast.id)
			);
		}, toastTime + 500);
	};

	const renderSavingBlock = () => {
		return (
			<AbortPopup>
				<Modal width="550px" zIndex="10000000">
					<ModalBody>
						Saving settings, please wait.<br />
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</ModalBody>
				</Modal>
			</AbortPopup>
		);
	};

	const renderUpdateAliasBlock = () => {
		return (
			<>
				<Modal width="768px" zIndex="10000000">
					<EditAlias
						row={aliasToEdit}
						industryOptions={metadataOptions.industries}
						onUpdateAlias={onUpdateAlias}
						onClose={setIsEditAlias}
						setAliasToEdit={setAliasToEdit}
						refetchAliasData={fetchMetaDataAndSettings}
					/>
				</Modal>
			</>
		);
	};

	if (isPipelineRunning) {
		return (
			<Wrapper>
				<Container>
					<Title>Benchmarks</Title>
					<p>
						Push to Benchmark pipeline is in progress. Aliases & metadata will
						be available when the current pipeline execution is complete.
					</p>
				</Container>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Container>
				<Title>Benchmarks</Title>
				{isChecking ? (
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				) : (
					<>
						{!isCategoryTree && (
							<Table>
								{isAliasFetching ? (
									<ApLoaderDefault loaderText={() => <ApLoaderText />} />
								) : (
									<>
										{aliasData === null ? (
											<p>Alias not available.</p>
										) : (
											<>
												{aliasData.length > 0 && (
													<AliasListTable
														data={aliasData}
														onRowSelected={onRowSelection}
														defaultAlias={defaultSettings.aliasName}
														onEditAlias={onEditAlias}
													/>
												)}
											</>
										)}
										{isEditAlias && renderUpdateAliasBlock()}
									</>
								)}
							</Table>
						)}
						{isCategoryTree && (
							<>
								<CategoryTree
									isCategoryFetching={isCategoryFetching}
									categoryTreeData={categoryTreeData}
									onChangeSelectionAll={onChangeSelectionAll}
									onChangeCategorySelection={onChangeCategorySelection}
								/>
								{isSettingsSaving && renderSavingBlock()}
							</>
						)}
						<Actions isTree={isCategoryTree}>
							{!isCategoryTree && (
								<>
									{isAliasFetching ? (
										<></>
									) : (
										<>
											{aliasData.length > 0 && (
												<ApButtonSecondary
													onClick={() =>
														setIsCategoryTree(pervState => !pervState)
													}
													disabled={
														defaultSettings.aliasName.length === 0 ||
														aliasData.length === 0
													}
												>
													Select Categories
												</ApButtonSecondary>
											)}
										</>
									)}
								</>
							)}
							{isCategoryTree && (
								<>
									<ApButtonSecondary
										onClick={() => {
											setIsCategoryTree(prevState => !prevState);
											setCategoryTreeData([]);
										}}
									>
										Back
									</ApButtonSecondary>
									{categoryTreeData.length !== 0 ? (
										<ApButtonSecondary
											onClick={() => {
												onSaveSettings();
											}}
											disabled={categoryTreeData.length > 0 ? false : true}
										>
											Save
										</ApButtonSecondary>
									) : (
										<></>
									)}
								</>
							)}
						</Actions>
						{toasts.map(toast => (
							<Toast
								key={toast.id}
								message={toast.message}
								status={toast.status}
								time={toastTime}
							/>
						))}
					</>
				)}
			</Container>
		</Wrapper>
	);
};

export default props => (
	<ToastMessageProvider>
		<Benchmark {...props} />
	</ToastMessageProvider>
);
